import { useEffect, useState } from 'react';

/** Entry animation length. Long enough to read as growth, short enough not to delay. */
const DURATION_MS = 550;

export interface ChartAnimation {
  isAnimationActive: boolean;
  animationDuration: number;
  animationEasing: 'ease-out';
}

const QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Animation props to spread onto Recharts series (`<Bar>`, `<Area>`, `<Line>`),
 * so every chart on a dashboard enters with the same timing.
 *
 * **Animates on mount only, then switches itself off.** That is not a
 * simplification — it's required. Recharts interpolates against the width it
 * measured when the animation started, so a re-measure while one is running
 * freezes the series part-drawn: bars stop at a fraction of their length and
 * stay there. On this dashboard re-measures are routine — resizing a tile and
 * opening a drilldown both dispatch one (see `DashboardGrid`) — so leaving
 * animation permanently on renders the charts *wrong*, not merely busier.
 *
 * Going static after the first pass also means a resize, a unit toggle or a
 * regrouping updates instantly, which is what you want once the reader is
 * interacting rather than arriving.
 *
 * Honours `prefers-reduced-motion`: those users get the final frame
 * immediately, and the listener keeps that live without a reload.
 */
export function useChartAnimation(): ChartAnimation {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(QUERY).matches,
  );
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent): void => setReduced(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    // A little past the animation, so the final frame has certainly landed
    // before the series goes static.
    const timer = window.setTimeout(() => setSettled(true), DURATION_MS + 150);
    return () => window.clearTimeout(timer);
  }, []);

  const active = !reduced && !settled;
  return {
    isAnimationActive: active,
    animationDuration: active ? DURATION_MS : 0,
    animationEasing: 'ease-out',
  };
}
